import { useState } from 'react';

import { FormControlLabel } from '@mui/material';

import { CAST_AI, CAST_PILOT_ASSISTANT } from '@cast/constants';
import {
  ActionsGroup,
  Button,
  Checkbox,
  DialogV2,
  ExternalLink,
  Inset,
  makeDialog,
  useCloseDialog,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';

import {
  CAST_PILOT_ASSISTANT_PRIVACY_URL,
  CAST_PILOT_ASSISTANT_TERMS_URL,
} from './castPilotAssistant';

// 'confirmed' is delivered to the caller's onClose only when the user accepts
// the terms and clicks Enable. Cancel / X / overlay close with NO_RESULT.
type EnableOpsPilotDialogResult = 'confirmed';

const EnableOpsPilotDialogContent = () => {
  const closeDialog = useCloseDialog<EnableOpsPilotDialogResult>();
  const [agreed, setAgreed] = useState(false);

  return (
    <DialogV2
      testId="enable-castpilot-assistant-dialog"
      title={`Enable ${CAST_PILOT_ASSISTANT}`}
      footerDivider
      endActions={
        <ActionsGroup>
          <Button
            variant="secondary"
            testId="enable-castpilot-assistant-cancel"
            onClick={() => closeDialog()}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!agreed}
            testId="enable-castpilot-assistant-confirm"
            onClick={() => closeDialog('confirmed')}
          >
            Enable {CAST_PILOT_ASSISTANT}
          </Button>
        </ActionsGroup>
      }
    >
      <Inset mb={24}>
        <Stack gap={16}>
          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
          >
            {CAST_PILOT_ASSISTANT} answers questions about your clusters by
            analyzing cluster metadata, cost data, infrastructure telemetry, and
            Kubernetes events. It activates only upon the user&apos;s clear
            consent.
          </Typography>
          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
          >
            If you turn on log access, {CAST_PILOT_ASSISTANT} can also read pod
            logs via your cluster proxy.
          </Typography>
          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
          >
            {CAST_PILOT_ASSISTANT} does not modify workloads, execute write
            operations, or take autonomous actions in your cluster.
          </Typography>
          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
          >
            Your data stays within {CAST_AI}. Models run on {CAST_AI}{' '}
            infrastructure. No data is sent to external AI providers and none is
            used to train AI models. Cost and infrastructure data may be
            included in generated responses to produce summaries.
          </Typography>
          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
          >
            Suggestions may be incomplete or incorrect. Validate all
            recommendations before applying them to production.
          </Typography>
          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
          >
            By enabling, you confirm you have authority to activate this feature
            for your organization.
          </Typography>

          <Stack direction="row" gap={16}>
            <ExternalLink link={CAST_PILOT_ASSISTANT_TERMS_URL}>
              {CAST_AI} Terms of Service
            </ExternalLink>
            <ExternalLink link={CAST_PILOT_ASSISTANT_PRIVACY_URL}>
              Privacy Policy
            </ExternalLink>
          </Stack>

          <FormControlLabel
            sx={{ gap: 8, ml: 0, alignItems: 'center' }}
            control={
              <Checkbox
                checked={agreed}
                onChange={(event) => setAgreed(event.target.checked)}
                testId="castpilot-assistant-terms-checkbox"
              />
            }
            label={
              <Typography
                color={tokens.theme.fg.structural.primary}
                variant="P12R"
              >
                I agree to the {CAST_AI} Terms of Service
              </Typography>
            }
          />
        </Stack>
      </Inset>
    </DialogV2>
  );
};

export const useEnableOpsPilotDialog = makeDialog<
  Record<string, never>,
  EnableOpsPilotDialogResult
>(EnableOpsPilotDialogContent);
